import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

interface HeaderProps {
  onHomeClick: () => void;
  onAboutClick: () => void;
  onServicesClick: () => void;
  onGalleryClick: () => void;
  onTestimonialsClick: () => void;
  onTimelineClick?: () => void;
  onEstimationClick?: () => void;
  onBookingClick?: () => void;
  onContactClick: () => void;
}

const Header = ({
  onHomeClick,
  onAboutClick,
  onServicesClick,
  onGalleryClick,
  onTestimonialsClick,
  onTimelineClick,
  onEstimationClick,
  onBookingClick,
  onContactClick
}: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 60) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      // Calculate active section
      const sections = document.querySelectorAll("section[id]");
      const scrollPosition = window.scrollY + 200;

      sections.forEach((section) => {
        const sectionId = section.getAttribute("id") || "";
        const sectionTop = section.getBoundingClientRect().top + window.scrollY;
        const sectionHeight = section.getBoundingClientRect().height;

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(sectionId);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const headerClass = `fixed w-full z-50 bg-white shadow-md transition-all duration-300 ${
    isScrolled ? "py-2" : "py-3"
  }`;

  return (
    <header className={headerClass}>
      <div className="container mx-auto px-4 flex justify-between items-center">
        <button onClick={onHomeClick} className="flex items-center">
          <img
            src="logo-e-zez-png-3.png"
            alt="Swiss Welding LLC"
            className="h-12 w-auto"
          />
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <button
            onClick={onHomeClick}
            className={`nav-link text-primary font-montserrat font-medium hover:text-red-600 transition-colors ${
              activeSection === "home" ? "active" : ""
            }`}
          >
            HOME
          </button>
          <button
            onClick={onAboutClick}
            className={`nav-link text-primary font-montserrat font-medium hover:text-red-600 transition-colors ${
              activeSection === "about" ? "active" : ""
            }`}
          >
            ABOUT
          </button>
          <button
            onClick={onServicesClick}
            className={`nav-link text-primary font-montserrat font-medium hover:text-red-600 transition-colors ${
              activeSection === "services" ? "active" : ""
            }`}
          >
            SERVICES
          </button>
          <button
            onClick={onGalleryClick}
            className={`nav-link text-primary font-montserrat font-medium hover:text-red-600 transition-colors ${
              activeSection === "gallery" ? "active" : ""
            }`}
          >
            GALLERY
          </button>
          <button
            onClick={onTestimonialsClick}
            className={`nav-link text-primary font-montserrat font-medium hover:text-red-600 transition-colors ${
              activeSection === "testimonials" ? "active" : ""
            }`}
          >
            TESTIMONIALS
          </button>
          <button
            onClick={onTimelineClick}
            className={`nav-link text-primary font-montserrat font-medium hover:text-red-600 transition-colors ${
              activeSection === "timeline" ? "active" : ""
            }`}
          >
            TIMELINE
          </button>
          <button
            onClick={onEstimationClick}
            className={`nav-link text-primary font-montserrat font-medium hover:text-red-600 transition-colors ${
              activeSection === "estimation" ? "active" : ""
            }`}
          >
            ESTIMATE
          </button>
          <button
            onClick={onBookingClick}
            className={`nav-link text-primary font-montserrat font-medium hover:text-red-600 transition-colors ${
              activeSection === "booking" ? "active" : ""
            }`}
          >
            BOOK NOW
          </button>
          <button
            onClick={onContactClick}
            className="bg-transparent hover:bg-white text-black font-montserrat font-semibold py-2 px-6 rounded-md transition-colors border-2 border-black flex items-center justify-center"
          >
            CONTACT US
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="md:hidden flex items-center"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h16M4 18h16"
            ></path>
          </svg>
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween" }}
            className="md:hidden fixed top-0 right-0 h-full w-64 bg-slate-900 z-50 shadow-xl py-4 px-6"
          >
            <div className="flex justify-end">
              <button
                onClick={closeMobileMenu}
                className="text-white text-2xl"
              >
                &times;
              </button>
            </div>
            <div className="flex flex-col space-y-6 mt-8">
              <button
                onClick={() => {
                  onHomeClick();
                  closeMobileMenu();
                }}
                className="text-white font-montserrat font-medium hover:text-gray-800 transition-colors text-left"
              >
                HOME
              </button>
              <button
                onClick={() => {
                  onAboutClick();
                  closeMobileMenu();
                }}
                className="text-white font-montserrat font-medium hover:text-gray-800 transition-colors text-left"
              >
                ABOUT
              </button>
              <button
                onClick={() => {
                  onServicesClick();
                  closeMobileMenu();
                }}
                className="text-white font-montserrat font-medium hover:text-gray-800 transition-colors text-left"
              >
                SERVICES
              </button>
              <button
                onClick={() => {
                  onGalleryClick();
                  closeMobileMenu();
                }}
                className="text-white font-montserrat font-medium hover:text-gray-800 transition-colors text-left"
              >
                GALLERY
              </button>
              <button
                onClick={() => {
                  onTestimonialsClick();
                  closeMobileMenu();
                }}
                className="text-white font-montserrat font-medium hover:text-gray-800 transition-colors text-left"
              >
                TESTIMONIALS
              </button>
              <button
                onClick={() => {
                  onTimelineClick && onTimelineClick();
                  closeMobileMenu();
                }}
                className="text-white font-montserrat font-medium hover:text-gray-800 transition-colors text-left"
              >
                TIMELINE
              </button>
              <button
                onClick={() => {
                  onEstimationClick && onEstimationClick();
                  closeMobileMenu();
                }}
                className="text-white font-montserrat font-medium hover:text-gray-800 transition-colors text-left"
              >
                ESTIMATE
              </button>
              <button
                onClick={() => {
                  onBookingClick && onBookingClick();
                  closeMobileMenu();
                }}
                className="text-white font-montserrat font-medium hover:text-gray-800 transition-colors text-left"
              >
                BOOK NOW
              </button>
              <button
                onClick={() => {
                  onContactClick();
                  closeMobileMenu();
                }}
                className="bg-transparent hover:bg-white hover:text-black text-white font-montserrat font-semibold py-2 px-6 rounded-md transition-colors border-2 border-white flex items-center justify-center"
              >
                CONTACT US
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;